import React from "react";
import { Helmet } from "react-helmet";
import PageHero from "@webcore/components/domain/content-site/sections/PageHero";
import WorkflowsSection, { WorkflowCardData } from "@webcore/components/domain/content-site/sections/WorkflowsSection";
import CalloutSection from "@webcore/components/domain/content-site/sections/CalloutSection";
import CtaSection from "@webcore/components/domain/content-site/sections/CtaSection";
import FaqSection from "@webcore/components/domain/content-site/sections/FaqSection";
import { saasFaqs } from "@/content/faqs";
import { taiSoftwareCite, taiSoftwareQuote } from "@/content/testimonials";

import pipelineReviewImg from "@/assets/workflows/pipeline-review.jpg";
import mrrCloseImg from "@/assets/workflows/mrr-close.jpg";
import commissionRunImg from "@/assets/workflows/commission-run.jpg";
import budgetVarianceImg from "@/assets/workflows/budget-variance.jpg";

const benefits: WorkflowCardData[] = [
  {
    num: "// 01",
    title: "SaaS Pipeline Forecasting",
    sub: "Pipeline forecasting you can trust — not stale Salesforce reports.",
    body: (
      <>
        Stop running forecast meetings on numbers nobody trusts. A{" "}
        <strong>weighted pipeline forecast</strong> by stage and product.{" "}
        <strong>Zombie-deal detection</strong> — stale, $0, or no-recent-activity deals flagged
        automatically. <strong>Forecast-vs-actual accuracy</strong> tracked at the rep and team
        level. Drill through from any tile into the underlying opportunities, owners, and source
        records.
      </>
    ),
    screenshot: pipelineReviewImg,
    replacesLabel: "Replaces or sits alongside",
    replacesItems: "Salesforce reports · Clari · forecast spreadsheets",
  },
  {
    num: "// 02",
    title: "MRR Rollforward",
    sub: "Built once and run forever — no more spreadsheet rebuilds.",
    body: (
      <>
        The monthly rollforward your finance team rebuilds by hand, automated and auditable.{" "}
        <strong>MRR movement</strong> classified into new logo, cross-sell, upsell, downgrade,
        and churn. <strong>Customer-level audit panel</strong> behind every cell.{" "}
        <strong>Override workflow</strong> for the judgment calls that don't fit the rules.
      </>
    ),
    screenshot: mrrCloseImg,
    replacesLabel: "Replaces",
    replacesItems: "the golden-truth spreadsheet · the three-day month-end cycle",
  },
  {
    num: "// 03",
    title: "Sales Commissions as Code",
    sub: "Commissions as versioned code — not a comp spreadsheet nobody trusts.",
    body: (
      <>
        A commission calculator built on a foundation where every number traces to its source.{" "}
        <strong>Per-rep monthly statements</strong>, fully auditable. New deals, prior-month
        deals with current MRR deltas applied to rates, and terminated deals zeroed
        automatically. <strong>Bonus tiers, accelerators, and clawback rules</strong> written as
        readable SQL — not buried in a BI tool's UI.
      </>
    ),
    screenshot: commissionRunImg,
    replacesLabel: "Replaces",
    replacesItems: "CaptivateIQ · QuotaPath · your homegrown comp sheet",
  },
  {
    num: "// 04",
    title: "Variance Reporting",
    sub: "Budget vs actuals, defended.",
    body: (
      <>
        Monthly budget compared against billed revenue and tracked expenses.{" "}
        <strong>Material variances flagged automatically</strong> against your thresholds, with{" "}
        <strong>variance attribution down to the line item</strong>. Drill from any flagged
        account to the vendors, bills, and source transactions behind it.
      </>
    ),
    screenshot: budgetVarianceImg,
    replacesLabel: "Replaces",
    replacesItems: "the FP&A team's monthly export-and-pivot ritual",
  },
];

const SolutionsSaas: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>SaaS Revenue Data Platform | Pipeline, MRR & Commissions — DataFabrIQ</title>
        <meta
          name="description"
          content="One governed, auditable source of truth for SaaS revenue — weighted pipeline forecasts, MRR rollforwards, and commissions as code. Replaces Fivetran + dbt + BI."
        />
        <link rel="canonical" href="https://datafabriq.co/solutions/saas" />
      </Helmet>

      <PageHero
        eyebrow="// solutions — saas & technology companies"
        heading={
          <>
            The single source of truth<br />
            for <span className="accent">SaaS revenue data.</span>
          </>
        }
        subheading={
          <>
            The BI tool is the last mile. The real value is the governed, auditable{" "}
            <em>data foundation</em> underneath it — and the AI workflows you build on top.
          </>
        }
        body={
          <>
            <p>
              DataFabrIQ turns your billing, CRM, accounting, and payments systems into{" "}
              <strong>typed business objects</strong> — customers, deals, subscriptions, revenue
              events, monthly rollforwards — that your team can query directly. It replaces the
              Fivetran + dbt + BI-tool stack with one platform.{" "}
              <strong>Every cell knows where it came from.</strong>
            </p>
          </>
        }
        primaryLabel="Talk to Sales →"
        secondaryLabel="Explore the platform"
        secondaryHref="/#platform"
      />

      <CalloutSection quote={taiSoftwareQuote} cite={taiSoftwareCite} variant="testimonial" />

      <WorkflowsSection
        id="benefits"
        eyebrow="// what you get"
        heading={
          <>
            The workflows SaaS teams<br />
            <span className="serif">run the business with.</span>
          </>
        }
        intro={
          <>
            Weighted pipeline forecasts, MRR rollforwards, and commissions as code — every number
            drillable to the source record, on the same governed data model.
          </>
        }
        workflows={benefits}
      />

      <CtaSection
        heading={
          <>
            One governed source of truth<br />
            for <span className="serif">SaaS revenue.</span>
          </>
        }
        body="See your pipeline, MRR rollforward, and commissions running on your own data inside a week."
        primaryLabel="Talk to Sales →"
        secondaryLabel="Explore the platform"
        secondaryHref="/#platform"
      />

      <FaqSection
        id="faq"
        eyebrow="// faq"
        heading={<>Questions, <span className="serif">answered.</span></>}
        faqs={saasFaqs}
      />
    </>
  );
};

export default SolutionsSaas;
